import { formatNumberWithDecimalLayout, getDecimalLayoutLocale } from './userPreferencesHelpers';

export const exchangeRates = {
  AUD: 1.65,
  BRL: 5.98,
  MXN: 18.45,
  USD: 1.08,
  PLN: 4.32,
  BDT: 118.5,
};

export const currencyInfo = {
  AUD: { symbol: 'A$', name: 'Australian Dollar', flag: '🇦🇺' },
  BRL: { symbol: 'R$', name: 'Brazilian Real', flag: '🇧🇷' },
  MXN: { symbol: '$', name: 'Mexican Peso', flag: '🇲🇽' },
  USD: { symbol: '$', name: 'US Dollar', flag: '🇺🇸' },
  PLN: { symbol: 'zł', name: 'Polish Złoty', flag: '🇵🇱' },
  BDT: { symbol: '৳', name: 'Bangladeshi Taka', flag: '🇧🇩' },
};

export const convertFromEUR = (amountEUR, targetCurrency) => {
  if (!amountEUR || !targetCurrency || !exchangeRates[targetCurrency]) {
    return 0;
  }
  return amountEUR * exchangeRates[targetCurrency];
};

const getCurrencyFlag = (currencyCode, currencies) => {
  if (!currencies || currencies.length === 0) return '';

  const normalizedCurrency = currencyCode.toUpperCase();
  const currency = currencies.find((c) => c.code === normalizedCurrency);
  if (!currency) return '';

  if (currency.country && currency.country.flag) {
    return currency.country.flag;
  }

  if (currency.countries && Array.isArray(currency.countries) && currency.countries.length > 0) {
    return currency.countries[0].flag || '';
  }

  return '';
};

export const getAvailableCurrencies = (configData = null) => {
  if (!configData || !configData.currencies || configData.currencies.length === 0) {
    return [];
  }

  return configData.currencies.map((currency) => {
    let flag = '';
    if (currency.country && currency.country.flag) {
      flag = currency.country.flag;
    } else if (currency.countries && Array.isArray(currency.countries) && currency.countries.length > 0) {
      flag = currency.countries[0].flag || '';
    } else {
      flag = getCurrencyFlag(currency.code, configData.currencies || []);
    }

    const countryNames = [];
    const countryCodes = [];
    if (currency.country?.name) countryNames.push(currency.country.name);
    if (currency.country?.code) countryCodes.push(currency.country.code);
    if (Array.isArray(currency.countries)) {
      currency.countries.forEach((country) => {
        if (country?.name) countryNames.push(country.name);
        if (country?.code) countryCodes.push(country.code);
      });
    }

    return {
      code: currency.code,
      symbol: currency.symbol,
      name: currency.name,
      flag,
      countryName: countryNames[0] || '',
      countryNames,
      countryCodes,
      fullDisplay: `${flag} ${currency.symbol} ${currency.code} - ${currency.name}`,
    };
  });
};

export function findCurrenciesMatchingQuery(configCurrencies, query, { limit = 6 } = {}) {
  const normalizedQuery = String(query || '')
    .trim()
    .toLowerCase();
  if (!normalizedQuery || !Array.isArray(configCurrencies) || configCurrencies.length === 0) {
    return [];
  }

  return configCurrencies
    .filter((currency) => Boolean(currency?.code))
    .map((currency) => {
      const code = String(currency.code).toLowerCase();
      const name = String(currency.name || '').toLowerCase();
      const countries = [
        ...(currency.country ? [currency.country] : []),
        ...(Array.isArray(currency.countries) ? currency.countries : []),
      ];
      const countryHit = countries.find((country) => {
        const countryName = String(country?.name || '').toLowerCase();
        const countryCode = String(country?.code || '').toLowerCase();
        return countryName.includes(normalizedQuery) || countryCode === normalizedQuery;
      });

      if (!(code.includes(normalizedQuery) || name.includes(normalizedQuery) || countryHit)) {
        return null;
      }

      return {
        code: currency.code,
        name: currency.name || currency.code,
        countryName: countryHit?.name || currency.country?.name || countries[0]?.name || '',
        countryCode: countryHit?.code || currency.country?.code || countries[0]?.code || '',
        matchedByCountry: Boolean(countryHit),
      };
    })
    .filter(Boolean)
    .slice(0, limit);
}

export function getCurrencyMatchLabel(match) {
  if (!match) return '';
  if (match.matchedByCountry && match.countryName) {
    return `${match.countryName} · ${match.code}`;
  }
  if (match.countryName) {
    return `${match.code} · ${match.countryName}`;
  }
  return match.name ? `${match.code} · ${match.name}` : match.code;
}

export const formatCurrency = (amount, currencyCode, localeOrPreferences = 'de-DE') => {
  if (amount === null || amount === undefined || !currencyCode) return '0.00';

  try {
    const locale =
      typeof localeOrPreferences === 'string'
        ? localeOrPreferences
        : getDecimalLayoutLocale(localeOrPreferences);

    const formattedNumber = new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
    return `${formattedNumber} ${currencyCode}`;
  } catch (error) {
    return `${formatNumberWithDecimalLayout(amount, localeOrPreferences)} ${currencyCode}`;
  }
};
